/**
 * Planetwide clouds baked into octahedral decks. Closed, overlapping faceted
 * lobes supply low-cloud silhouettes, thickness and directional shading;
 * stretched fields supply the thin upper streaks. The display still costs one
 * filtered lookup per deck. The existing altitude-gated volume re-lofts that
 * same coverage into slabs for flight, and the low atlas feeds ground shadows.
 *
 * Weather owns coverage and rain-bearing optical depth. Seeded shapes drift on
 * the shared epoch, independently of loading and frame rate. Sun/moon exposure
 * and sunset warmth use the production sky's inputs.
 */
import * as THREE from "three";
import { WEATHER_DRIFT } from "@hyperlight/shared";
import { WEATHER_MAP_GLSL, WeatherField, weatherTime, WEATHER } from "./weatherField";
import { CLOUD_MAP_SIZE, CLOUD_MAPPING_GLSL } from "./cloudMapping";
import { CLOUD_LIGHT_GLSL } from "./cloudLight";
export { setWeatherTime } from "./weatherField";

// Normal: 1024² / 512² / 256². High quality doubles only the main bank's atlas.
// Lighting is baked from solar elevation at each cloud column, including twilight.
const REBAKE_MIN_DT = 0.4;

// Cloud decks, low → high. Altitude is a multiple of atmosphere height (which is
// ~4-9% of planet radius). Lower decks: bigger, denser, slower, more opaque. Higher:
// smaller, sparser, wispier, faster — so the sky reads as layered cumulus + cirrus.
interface LayerCfg {
  alt: number; // altitude = planetRadius + atmoHeight * alt
  thick: number; // physical half-thickness of the deck's slab (× atmoHeight) — the near-field volume
  scale: number; // base cloud size (bigger = smaller clouds)
  coverage: number; // multiplier of the shared cloud coverage
  wispy: number; // 0 rounded low clouds … 1 stretched thin high clouds
  opacity: number; // display alpha multiplier
  windMul: number; // drift-speed multiplier
  seedOff: number; // noise offset so decks aren't copies of each other
}
// Multipliers of the shared spatial weather coverage, not fixed sky conditions.
const LAYERS: LayerCfg[] = [
  { alt: 1.0, thick: 0.4, scale: 36, coverage: 1, wispy: 0, opacity: 0.98, windMul: 0.8, seedOff: 0.0 },
  { alt: 1.7, thick: 0.32, scale: 32, coverage: 0.38, wispy: 0.85, opacity: 0.35, windMul: 1.3, seedOff: 21.7 },
  { alt: 2.6, thick: 0.45, scale: 48, coverage: 0.25, wispy: 1, opacity: 0.22, windMul: 2.1, seedOff: 47.3 },
];

export interface CloudUniforms {
  uDay: { value: number };
  uSunset: { value: number };
  uSunLocal: { value: THREE.Vector3 };
  uMoonLocal: { value: THREE.Vector3 };
  uMoonLight: { value: THREE.Vector3 };
  uSunVisibility: { value: number };
}

/** Near-field volume: re-lofts the baked decks into 3D around the camera so you fly
 *  THROUGH clouds (not past flat sheets). Gated by altitude — only on near the decks. */
export interface CloudVolume {
  mesh: THREE.Mesh;
  uCamLocal: { value: THREE.Vector3 }; // camera position in planet-local space (poke each frame)
  uEdgeFade: { value: number }; // 0..1, ramped at the band edges so the volume never pops in
  bandLo: number; // surface-dist (altitude) window where the volume is active
  bandHi: number;
  fadeMargin: number; // altitude over which uEdgeFade ramps 0→1 at each band edge
}

export interface CloudShell {
  mesh: THREE.Object3D; // a group holding every deck dome + the near-field volume
  uniforms: CloudUniforms;
  volume: CloudVolume;
  /** The main shadow-casting deck (the low one): its coverage texture + sphere radius,
   *  so the terrain shader can darken the ground where cloud sits between it and the sun. */
  shadow: { tex: THREE.Texture; radius: number; frame: THREE.Matrix3 };
  /** Cheap per-frame transport between the expensive lighting/shape bakes. */
  update: (time: number) => void;
  /** Render every deck's volumetric march into its target. Reads `uSunLocal` live. */
  bake: (renderer: THREE.WebGLRenderer, time: number) => void;
  /** Free the render targets + materials (call when the planet is torn down). */
  dispose: () => void;
}

// GLSL value-noise fbm (the sun-granulation idiom) + the volumetric cloud march.
const BAKE_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uSunLocal;   // direction TO the sun, planet-local
  uniform vec3 uWind;       // slow advection in noise space
  uniform float uSeed;      // per-deck noise offset
  uniform float uCoverage;  // 0..1 fraction of sky clouded
  uniform float uScale;     // base cloud size (bigger = smaller clouds)
  uniform float uWispy;
  uniform vec3 uAxis;
  uniform float uDrift;
  uniform vec3 uMoonLocal, uMoonLight;
  uniform float uHorizonDip, uSunVisibility;
  ${CLOUD_LIGHT_GLSL}
  ${WEATHER_MAP_GLSL}
  ${CLOUD_MAPPING_GLSL}
  vec4 columnWeather;
  vec3 cloudRotate(vec3 p, vec3 axis, float angle) {
    float c = cos(angle), s = sin(angle);
    return p * c + cross(axis, p) * s + axis * dot(axis, p) * (1.0 - c);
  }

  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  // Continuous scalar field, with planar gradients inside tetrahedra. These
  // planes belong to the cloud, not a screen-space overlay that swims on it.
  vec4 cloudPlane(vec3 p) {
    vec3 i = floor(p), f = fract(p);
    vec3 order = vec3(step(f.y, f.x), step(f.z, f.y), 1.0 - step(f.z, f.x));
    vec3 hi = min(order, 1.0 - order.zxy);
    vec3 lo = max(order, 1.0 - order.zxy);
    float a = hash(i), b = hash(i + hi), c = hash(i + lo), d = hash(i + 1.0);
    vec3 g = (b - a) * hi + (c - b) * (lo - hi) + (d - c) * (1.0 - lo);
    return vec4(g, a + dot(g, f));
  }
  vec4 cloudShape(vec3 p) {
    const mat3 turn = mat3(0.0, 0.8, 0.6, -0.8, 0.36, -0.48, -0.6, -0.48, 0.64);
    vec4 bank = cloudPlane(p * 0.68);
    vec4 lobe = cloudPlane(turn * p * 1.65 + vec3(7.1, 3.7, 1.3));
    // Quiet small detail only breaks the outline, not every lit face.
    float edge = cloudPlane(turn * p * 3.4 + vec3(2.8, 9.2, 5.6)).w;
    vec3 g = bank.xyz * (0.68 * 0.72) + (lobe.xyz * turn) * (1.65 * 0.24);
    return vec4(g, bank.w * 0.72 + lobe.w * 0.24 + edge * 0.04);
  }

  void cloudSlab(vec3 n, vec3 q, vec3 ray, float radius,
    inout float enter, inout float leave, inout vec3 normal) {
    float side = dot(n, q), velocity = dot(n, ray);
    if (abs(velocity) < 0.00001) {
      if (abs(side) > radius) leave = -100.0;
      return;
    }
    float a = (-radius - side) / velocity, b = (radius - side) / velocity;
    float nearT = min(a, b);
    if (nearT > enter) { enter = nearT; normal = velocity > 0.0 ? -n : n; }
    leave = min(leave, max(a, b));
  }
  // Project overlapping convex lobes into the existing atlas. Each lobe has
  // actual planar faces and a closed silhouette, not a lit noise contour.
  // Their radii follow a slower bank field so they collect into uneven masses.
  vec4 cloudLobes(vec3 p, vec3 ray, float coverage) {
    vec3 cell = floor(p), bestNormal = -ray;
    float first = 100.0, thickness = 0.0;
    float threshold = mix(0.74, 0.20, coverage);
    for (int z = -1; z <= 1; z++) for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
      vec3 id = cell + vec3(float(x), float(y), float(z));
      vec3 jitter = vec3(hash(id), hash(id + 19.7), hash(id + 37.3));
      vec3 centre = id + 0.15 + jitter * 0.7;
      float bank = cloudPlane(centre * 0.24 + vec3(3.1, 7.7, 1.9)).w;
      float growth = smoothstep(threshold - 0.10, threshold + 0.12, bank);
      float radius = (0.62 + jitter.y * 0.25) * growth;
      vec3 q = p - centre;
      // Loft centres onto the deck and taper away off-deck candidates smoothly.
      // Clipping every lobe at one radial plane would flatten all its shading.
      float radial = dot(q, ray);
      radius *= 1.0 - smoothstep(0.2, 0.9, abs(radial));
      q -= ray * radial;
      if (radius < 0.05 || dot(q, q) > radius * radius * 1.6) continue;
      float angle = jitter.x * 6.28318530718;
      q = cloudRotate(q, ray, angle);
      float enter = -100.0, leave = 100.0;
      vec3 normal = -ray;
      cloudSlab(vec3(1,0,0), q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(0,1,0), q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(0,0,1), q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(1,1,0) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(1,-1,0) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(1,0,1) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(1,0,-1) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(0,1,1) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(0,1,-1) * 0.70710678, q, ray, radius, enter, leave, normal);
      cloudSlab(vec3(1,1,1) * 0.57735027, q, ray, radius * 1.02, enter, leave, normal);
      cloudSlab(vec3(-1,1,1) * 0.57735027, q, ray, radius * 1.02, enter, leave, normal);
      cloudSlab(vec3(1,-1,1) * 0.57735027, q, ray, radius * 1.02, enter, leave, normal);
      cloudSlab(vec3(1,1,-1) * 0.57735027, q, ray, radius * 1.02, enter, leave, normal);
      if (leave > enter) {
        thickness = max(thickness, leave - enter);
        if (enter < first) { first = enter; bestNormal = cloudRotate(normal, ray, -angle); }
      }
    }
    return vec4(bestNormal, thickness);
  }

  void main() {
    vec3 dir = cloudDirection(vUv);
    // Bake in the moving cloud frame. A stable atlas grid avoids resampling
    // the silhouette at every bake; only weather and light need refreshing.
    columnWeather = weatherMap(cloudRotate(dir, uAxis, -uDrift));
    // Rotate light with the shape domain so shading stays attached to the sun.
    vec3 lightDir = cloudRotate(uSunLocal, uAxis, uDrift);
    vec3 stretch = normalize(uWind);
    vec3 base = dir * uScale;
    base -= stretch * dot(base, stretch) * uWispy * 0.87;
    // Keep the faceted lobe geometry stable in its moving frame. Nudging this
    // domain only on bake frames changed the winning lobe face abruptly, even
    // with smooth atlas transport. Weather still grows/clears the cloud mass.
    base += vec3(uSeed) + uWind * sin(uSeed) * 4.0;
    float coverage = columnWeather.r * uCoverage;
    vec4 shape = cloudShape(base);
    float threshold = mix(0.68, 0.25, coverage);
    float mass = max(0.0, shape.w - threshold);
    // Rain keeps a continuous supporting base. Fair clouds have solid cores
    // and empty gaps, instead of only bright rims around a translucent centre.
    float depth = max(mass * 34.0, columnWeather.g * uCoverage * 4.0);
    float alpha = (1.0 - exp(-depth * (1.0 + columnWeather.b * 3.0)))
      * smoothstep(0.0, 0.025, coverage);
    alpha *= mix(1.0, 0.65, uWispy);
    // Chain rule for the anisotropic stretch, then project onto the sphere.
    vec3 gradient = shape.xyz - stretch * dot(shape.xyz, stretch) * uWispy * 0.87;
    vec3 tangent = gradient - dir * dot(gradient, dir);
    vec3 normal = normalize(-tangent * 3.8 - dir * mix(0.22, 0.06, uWispy));
    if (uWispy < 0.5) {
      const mat3 turn = mat3(0.0, 0.8, 0.6, -0.8, 0.36, -0.48, -0.6, -0.48, 0.64);
      vec4 lobes = cloudLobes(turn * base, turn * dir, coverage);
      normal = lobes.xyz * turn;
      depth = max(lobes.w * 7.0, columnWeather.g * uCoverage * 4.0);
      alpha = (1.0 - exp(-depth * (1.0 + columnWeather.b * 3.0))) * smoothstep(0.0, 0.025, coverage);
    }
    if (alpha < 0.002) { gl_FragColor = vec4(0.0); return; }
    float facing = smoothstep(-0.25, 0.8, dot(normal, lightDir));
    float highSun = smoothstep(0.1, 0.85, dot(dir, lightDir));
    float light = clamp(facing * 0.85 + highSun * 0.15, 0.0, 1.0);
    light = mix(light, 0.48, columnWeather.g * 0.6);
    vec3 shadowCol = mix(vec3(0.43, 0.48, 0.59), vec3(0.34, 0.39, 0.48), columnWeather.g);
    float solarHeight = dot(dir, lightDir);
    float daylight = smoothstep(-.16, .20, solarHeight) * uSunVisibility;
    // Elevated decks retain direct light after the ground enters twilight.
    float sunlight = smoothstep(-uHorizonDip - .035, .12, solarHeight) * uSunVisibility;
    float roseShade = (1.0 - smoothstep(-.02, .16, solarHeight)) * daylight;
    shadowCol = mix(shadowCol, vec3(.39, .34, .49), roseShade * .65);
    vec3 moonDir = cloudRotate(uMoonLocal, uAxis, uDrift);
    float moonFace = smoothstep(-.25, .8, dot(normal, moonDir));
    // Cool, readable moonlit banks: preserve planar faces rather than crushing
    // the whole cloud to charcoal. Moonless ambient remains much dimmer than
    // daylight. The same atlas lights the fly-through volume and distant shell.
    float moonAbove = smoothstep(-.10, .18, dot(dir, moonDir));
    float moonEnergy = clamp(max(uMoonLight.r, max(uMoonLight.g,uMoonLight.b))*5.0,0.0,1.0)*moonAbove;
    float rim = pow(moonFace,3.0)*(1.0-exp(-depth*.7));
    vec3 night = mix(vec3(.021,.030,.064),vec3(.050,.063,.118),moonFace);
    night += moonEnergy*mix(vec3(.017,.023,.042),vec3(.12,.14,.22),moonFace*.7+rim*.3);
    night *= mix(1.0,.70,columnWeather.g);
    vec3 ambient = mix(night, shadowCol, daylight);
    vec3 lit = cloudSolarTint(solarHeight) * sunlight;
    gl_FragColor = vec4(clamp(mix(ambient, lit, light * sunlight), 0.0, 1.0) * alpha, alpha);
  }
`;

const BAKE_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const DISPLAY_VERT = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  #include <fog_pars_vertex>
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    #ifdef USE_FOG
      vFogDepth = -mv.z;
    #endif
    #include <logdepthbuf_vertex>
  }
`;

const DISPLAY_FRAG = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  #include <fog_pars_fragment>
  uniform float uDay, uSunset, uOpacity;
  uniform vec3 uTint;
  uniform vec3 uMoonLight;
  uniform sampler2D uCover;
  uniform mat3 uCloudFrame;
  varying vec3 vDir;
  ${CLOUD_MAPPING_GLSL}
  void main() {
    #include <logdepthbuf_fragment>
    vec3 dir = normalize(vDir);
    vec2 uv = cloudUv(uCloudFrame * dir);
    vec4 cl = texture2D(uCover, uv);
    if (cl.a < 0.01) { gl_FragColor = vec4(0.0); return; } // clear sky (cheap)
    vec3 col = cl.rgb / cl.a;
    gl_FragColor = vec4(col, cl.a * uOpacity);
    // Ground fog becomes opaque at sky-dome distances. Preserve aerial
    // perspective without erasing every far cloud into the background colour.
    #ifdef USE_FOG
      #ifdef FOG_EXP2
        float skyFog = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
      #else
        float skyFog = smoothstep(fogNear, fogFar, vFogDepth);
      #endif
      gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, min(skyFog, mix(.14,.48,smoothstep(.0,.3,uDay))));
    #endif
    // The atlas stores linear light. Production's sRGB post target needs the
    // same output transfer as terrain; otherwise night faces are crushed and
    // only the background is encoded. Linear GPU fixtures use the identity.
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

// --- shared offscreen bake rig: one fullscreen quad + one shape material, reused for
// every deck of every planet (uniforms are set per bake). ---
interface BakeRig {
  scene: THREE.Scene;
  cam: THREE.OrthographicCamera;
  mat: THREE.ShaderMaterial;
}
let _rig: BakeRig | null = null;
function bakeRig(): BakeRig {
  if (!_rig) {
    const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const mat = new THREE.ShaderMaterial({
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uSunLocal: { value: new THREE.Vector3(0, 1, 0) },
        uWind: { value: new THREE.Vector3() },
        uSeed: { value: 0 },
        uCoverage: { value: 0.5 },
        uScale: { value: 3.0 },
        uWispy: { value: 0 },
        uAxis: { value: new THREE.Vector3(0, 1, 0) },
        uDrift: { value: 0 },
        uMoonLocal: { value: new THREE.Vector3(0, 1, 0) },
        uMoonLight: { value: new THREE.Vector3() },
        uHorizonDip: { value: .15 },
        uSunVisibility: { value: 1 },
      },
      vertexShader: BAKE_VERT,
      fragmentShader: BAKE_FRAG,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
    quad.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(quad);
    _rig = { scene, cam, mat };
  }
  return _rig;
}

interface Deck {
  rt: THREE.WebGLRenderTarget;
  cfg: LayerCfg;
  wind: THREE.Vector3;
  seed: number;
  mesh: THREE.Mesh;
  mat: THREE.ShaderMaterial;
}

export function makeCloudShell(
  planetRadius: number,
  atmoHeight: number,
  atmoColor: readonly [number, number, number],
  seed: number,
  weather: WeatherField,
): CloudShell {
  const tint = new THREE.Vector3(
    1.0 * 0.7 + atmoColor[0] * 0.3,
    0.46 * 0.7 + atmoColor[1] * 0.3,
    0.18 * 0.7 + atmoColor[2] * 0.3,
  );

  // Shared across every deck: sun direction feeds the bake; day/sunset feed the display.
  const uSunLocal = { value: new THREE.Vector3(0, 1, 0) };
  const uDay = { value: 1 };
  const uSunset = { value: 0 };
  const uMoonLocal = { value: new THREE.Vector3(0, 1, 0) };
  const uMoonLight = { value: new THREE.Vector3() };
  const uSunVisibility = { value: 1 };

  const group = new THREE.Group();
  group.frustumCulled = false;
  const decks: Deck[] = [];

  LAYERS.forEach((cfg, i) => {
    const size = CLOUD_MAP_SIZE / (1 << i);
    const rt = new THREE.WebGLRenderTarget(size, size, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
    });
    rt.texture.wrapS = rt.texture.wrapT = THREE.ClampToEdgeWrapping;
    rt.texture.generateMipmaps = false;
    rt.texture.colorSpace = THREE.LinearSRGBColorSpace;

    const s = seed + cfg.seedOff;
    const wind = new THREE.Vector3(Math.sin(s * 1.3), Math.sin(s * 0.7) * 0.4, Math.cos(s * 1.3))
      .normalize()
      .multiplyScalar(0.012 * cfg.windMul);

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      fog: true,
      side: THREE.BackSide,
      uniforms: Object.assign(THREE.UniformsUtils.clone(THREE.UniformsLib.fog), {
        uDay,
        uSunset,
        uMoonLight,
        uCover: { value: rt.texture },
        uCloudFrame: { value: new THREE.Matrix3() },
        uTint: { value: tint },
        uOpacity: { value: cfg.opacity },
      }),
      vertexShader: DISPLAY_VERT,
      fragmentShader: DISPLAY_FRAG,
    });

    const mesh = new THREE.Mesh(new THREE.SphereGeometry(planetRadius + atmoHeight * cfg.alt, 96, 64), mat);
    // Nearer decks (lower) draw last so they blend OVER the farther ones; all under the
    // atmosphere shell (renderOrder 5).
    mesh.renderOrder = 4 + (LAYERS.length - 1 - i) * 0.1;
    mesh.frustumCulled = false;
    group.add(mesh);

    decks.push({ rt, cfg, wind, seed: (s % 97) * 1.37, mesh, mat });
  });

  // ---- Near-field volume: re-loft the baked decks into 3D around the camera ----
  // Mirrors the atmosphere shell (BackSide sphere, camera in planet-LOCAL space, analytic
  // sphere exit, log-depth). Per pixel it marches the deck SLABS from the camera, sampling
  // each deck's baked RT (lit colour + coverage in alpha) shaped by a vertical profile — so
  // you fly INTO real thickness, and the view whitens out only where local coverage is high.
  const N = LAYERS.length;
  const deckR = decks.map((d) => planetRadius + atmoHeight * d.cfg.alt);
  const deckT = decks.map((d) => atmoHeight * d.cfg.thick);
  const volOuter = planetRadius + atmoHeight * (LAYERS[N - 1].alt + LAYERS[N - 1].thick + 0.1);
  const bandLo = atmoHeight * (LAYERS[0].alt - LAYERS[0].thick - 0.1);
  const bandHi = atmoHeight * (LAYERS[N - 1].alt + LAYERS[N - 1].thick + 0.15);
  const uCamLocal = { value: new THREE.Vector3(0, 0, planetRadius) };
  const uEdgeFade = { value: 0 };
  const fadeMargin = atmoHeight * 0.25; // zero contribution at the surface, smooth cloud entry

  const samplerDecls = decks.map((_, k) => `uniform sampler2D uCover${k}; uniform mat3 uCloudFrame${k};`).join("\n      ");
  const sampleBranches = decks
    .map(
      (_, k) => `
        { float h = (r - (uDeckR[${k}] - uDeckT[${k}])) / (2.0 * uDeckT[${k}]);
          if (h > 0.0 && h < 1.0) { vec4 c = texture2D(uCover${k}, cloudUv(uCloudFrame${k} * (p / r))); float a = c.a * prof(h); if (a > acc.a) acc = vec4(c.rgb / max(c.a, 0.001), a); } }`,
    )
    .join("");

  const volUniforms: { [k: string]: THREE.IUniform } = {
    uCamLocal,
    uEdgeFade,
    uDay,
    uSunset,
    uMoonLight,
    uTint: { value: tint },
    uRo: { value: volOuter },
    uRange: { value: atmoHeight * 4.5 },
    uDensity: { value: 2.0 / atmoHeight }, // whiteout intensity — flown-through cloud you can still see through
    uDeckR: { value: deckR },
    uDeckT: { value: deckT },
  };
  decks.forEach((d, k) => {
    volUniforms[`uCover${k}`] = { value: d.rt.texture };
    volUniforms[`uCloudFrame${k}`] = d.mat.uniforms.uCloudFrame;
  });

  const volMat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    // The cloud is along the view ray, NOT at the sphere surface — so the mesh's geometry
    // depth is meaningless. Without this, terrain in front of the FAR sphere face wrongly
    // depth-culls the near cloud. The march is bounded to the slab (above terrain), and the
    // alpha is 0 in gaps, so rendering as a depth-agnostic overlay composites correctly.
    depthTest: false,
    fog: false,
    side: THREE.BackSide,
    uniforms: volUniforms,
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vObj;
      void main() {
        vObj = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_fragment>
      uniform vec3 uCamLocal, uTint, uMoonLight;
      uniform float uDay, uSunset, uRo, uRange, uDensity, uEdgeFade;
      uniform float uDeckR[${N}];
      uniform float uDeckT[${N}];
      ${samplerDecls}
      ${CLOUD_MAPPING_GLSL}
      varying vec3 vObj;
      float prof(float h) { return smoothstep(0.0, 0.25, h) * (1.0 - smoothstep(0.5, 1.0, h)); }
      vec4 sampleDecks(vec3 p) {
        float r = length(p);
        vec4 acc = vec4(0.0);
        ${sampleBranches}
        return acc;
      }
      void main() {
        #include <logdepthbuf_fragment>
        vec3 ro = uCamLocal;
        vec3 rd = normalize(vObj - ro);
        float b = dot(ro, rd), c2 = dot(ro, ro);
        float disc = b * b - (c2 - uRo * uRo);
        if (disc <= 0.0) discard;
        float tMax = min(-b + sqrt(disc), uRange);
        if (tMax <= 0.0) discard;
        const int STEPS = 32;
        float dt = tMax / float(STEPS);
        float T = 1.0;
        vec3 lum = vec3(0.0);
        for (int i = 0; i < STEPS; i++) {
          float t = (float(i) + 0.5) * dt;
          vec4 s = sampleDecks(ro + rd * t);
          if (s.a > 0.001) {
            float fade = 1.0 - smoothstep(uRange * 0.55, uRange, t); // hand off to the skybox decks far away
            float a = clamp(s.a * uDensity * dt, 0.0, 1.0) * fade;
            lum += T * s.rgb * a;
            T *= 1.0 - a;
            if (T < 0.02) break;
          }
        }
        float alpha = (1.0 - T) * uEdgeFade; // edge fade ramps the volume in/out by altitude (no pop)
        if (alpha < 0.003) discard;
        vec3 col = lum / (1.0 - T);
        gl_FragColor = vec4(col, alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  const volMesh = new THREE.Mesh(new THREE.SphereGeometry(volOuter, 64, 48), volMat);
  volMesh.renderOrder = 4.8; // over the flat decks (4.x), under the atmosphere shell (5)
  volMesh.frustumCulled = false;
  volMesh.visible = false; // gated on by altitude in Planet.updateAtmosphereCam
  group.add(volMesh);

  const axis = new THREE.Vector3().fromArray(weather.def.spin.axis).normalize();
  const rotation = new THREE.Matrix4();
  function update(time: number): void {
    // Evaluate a bounded shared-epoch phase in JS doubles, not in a float GPU
    // clock. Sky, flight fog and shadows all sample this same moving atlas.
    for (const d of decks) {
      rotation.makeRotationAxis(axis, -((time * WEATHER_DRIFT * d.cfg.windMul) % (Math.PI * 2)));
      d.mat.uniforms.uCloudFrame.value.setFromMatrix4(rotation);
    }
  }

  function bake(renderer: THREE.WebGLRenderer, _time: number): void {
    weather.update(weatherTime());
    const rig = bakeRig();
    const u = rig.mat.uniforms;
    Object.assign(u, weather.uniforms);
    const prevRT = renderer.getRenderTarget();
    for (let i = 0; i < decks.length; i++) {
      const d = decks[i];
      const size = CLOUD_MAP_SIZE / (1 << i) * (i === 0 && WEATHER.quality === "high" ? 2 : 1);
      if (d.rt.width !== size) d.rt.setSize(size, size);
      u.uSunLocal.value.copy(uSunLocal.value);
      u.uMoonLocal.value.copy(uMoonLocal.value);
      u.uMoonLight.value.copy(uMoonLight.value);
      u.uSunVisibility.value=uSunVisibility.value;
      // Bounded artistic atmosphere height: retain a dusk afterglow, not an
      // entire night of daylight on these deliberately compact planets.
      const ratio=planetRadius/(planetRadius+atmoHeight*d.cfg.alt);
      u.uHorizonDip.value=Math.min(.22,Math.sqrt(Math.max(0,1-ratio*ratio)));
      u.uWind.value.copy(d.wind);
      u.uSeed.value = d.seed;
      u.uCoverage.value = d.cfg.coverage;
      u.uScale.value = d.cfg.scale;
      u.uWispy.value = d.cfg.wispy;
      u.uAxis.value.fromArray(weather.def.spin.axis).normalize();
      u.uDrift.value = -((weatherTime() * WEATHER_DRIFT * d.cfg.windMul) % (Math.PI * 2));
      renderer.setRenderTarget(d.rt);
      renderer.render(rig.scene, rig.cam);
    }
    renderer.setRenderTarget(prevRT);
    update(weatherTime());
  }

  function dispose(): void {
    for (const d of decks) {
      d.rt.dispose();
      d.mat.dispose();
      d.mesh.geometry.dispose();
    }
    volMat.dispose();
    volMesh.geometry.dispose();
  }

  return {
    mesh: group,
    uniforms: { uDay, uSunset, uSunLocal, uMoonLocal, uMoonLight, uSunVisibility },
    volume: { mesh: volMesh, uCamLocal, uEdgeFade, bandLo, bandHi, fadeMargin },
    shadow: { tex: decks[0].rt.texture, radius: deckR[0], frame: decks[0].mat.uniforms.uCloudFrame.value },
    update,
    bake,
    dispose,
  };
}

export { REBAKE_MIN_DT };
